import VuexPersistence from 'vuex-persist';
import { safeLocalStorage } from '../../utils/safeStorage';

// Expiration time in hours (24 hours)
export const STORAGE_EXPIRATION_HOURS = 24;

// Namespace used while no Lead exists yet (cold start). Fixed rather than URL-derived so a
// single cold visit owns exactly one namespace, which the hand-off migration then empties.
// Exported because that migration has to name it explicitly: by the time it runs the lead
// key is rendered, so getProfileKey() no longer resolves to it.
export const COLD_START_PROFILE_KEY = 'cold';

// Namespace for a restricted continuation (an existing lead the server holds behind the `-`
// sentinel). Fixed for the same reason as the cold one: the page has no key and the path segment
// is the shared sentinel. Emptied on every fresh cold arrival (see leadgen/v4.blade.php).
export const RESTRICTED_PROFILE_KEY = 'restricted';

/**
 * Get the profile key this browser's stored form data belongs to.
 *
 * The SERVER-RENDERED key wins over the URL. On v4 with the Meta pixel enabled the path is
 * deliberately keyless (/lg-signup/v4/-), because the key is an unauthenticated credential and
 * the pixel reports the page location — so deriving the namespace from the path there would give
 * every applicant in a browser the SAME namespace, letting one person's persisted name, email,
 * address and income be restored into another person's form.
 *
 * On a cold start (paid social, no Lead yet) there is no rendered key and the router base
 * carries none either, so the path fallback would give every step its OWN namespace and hand
 * all of them to the next visitor of this browser — one person's saved name and email
 * prefilling another's form for the 24h TTL. A fixed namespace instead keeps the whole cold
 * visit in one place that the hand-off migration moves into the real key and deletes.
 *
 * The URL remains the fallback, and stays version-aware: it matches any /lg-signup/v{N}/{key}
 * path (v2, v4, ...), which is what the v2 funnel and any bundle rendered without the global
 * still rely on.
 */
export function getProfileKey() {
    const renderedKey = typeof window !== 'undefined' && window.lead && window.lead.key;
    if (renderedKey) {
        return String(renderedKey);
    }

    if (typeof window !== 'undefined' && window.__leadgenColdStart__) {
        return COLD_START_PROFILE_KEY;
    }

    if (typeof window !== 'undefined' && window.__leadgenRestricted__) {
        return RESTRICTED_PROFILE_KEY;
    }

    const pathMatch = window.location.pathname.match(/\/lg-signup\/(v\d+)\/([^/]+)/);
    if (pathMatch && pathMatch[2]) {
        return pathMatch[2];
    }
    return 'default';
}

/**
 * Generate a storage key namespaced by profile key for session isolation.
 */
export function getStorageKey(baseKey) {
    const profileKey = getProfileKey();
    return `${baseKey}_${profileKey}`;
}

/**
 * Check if stored data has expired.
 */
export function isExpired(storedData) {
    if (!storedData || !storedData._expiresAt) {
        return true;
    }
    return Date.now() > storedData._expiresAt;
}

/**
 * Whether this browser has genuine, non-expired prior progress for the
 * current profile — i.e. the user actually started this signup before in
 * this browser, as opposed to the server/profile already knowing their
 * name/email (e.g. from a marketing profile match).
 *
 * Must be called before any Vuex mutation is committed on mount: the first
 * commit triggers vuex-persist's save subscriber, which would otherwise
 * create a fresh localStorage entry and make every visitor look like a
 * returning one. Used to gate the mount-time resume guard so a first-time
 * visitor whose name/email are already known isn't fast-forwarded past
 * steps they've never actually seen (e.g. straight to the housing step).
 */
export function hasPersistedFormData(baseKey) {
    try {
        const stored = localStorage.getItem(getStorageKey(baseKey));
        if (!stored) {
            return false;
        }
        return !isExpired(JSON.parse(stored));
    } catch {
        return false;
    }
}

/**
 * Sanitize state before storage (remove sensitive data like full SSN / password /
 * bank account number). Shared by the v2 and v4 stores, which use the same
 * state shape (contact / account / bank).
 */
export function sanitizeLeadgenState(state) {
    if (!state) return state;

    let sanitized;
    try {
        sanitized = JSON.parse(JSON.stringify(state));
    } catch (e) {
        window.Bugsnag?.notify(e, (event) => {
            event.severity = 'error';
            event.context = 'sanitizeLeadgenState';
            event.addMetadata('state', { keys: Object.keys(state) });
        });
        return null;
    }

    if (sanitized.contact) {
        // Strip the full SSN for PCI. We deliberately do NOT keep a last4 remnant:
        // nothing in v2/v4 ever reads ssn_last4, and because the outbound payload is
        // built by spreading the whole contact slice, a lingering ssn_last4 would ride
        // back into personal.* on resumed sessions and pollute request_data. The full
        // SSN is always re-collected before submit (and kept tab-scoped by
        // createSsnSessionPlugin across same-tab reloads).
        delete sanitized.contact.ssn;
    }

    if (sanitized.account) {
        delete sanitized.account.password;
    }

    // The bank account number identifies a specific account and is PCI, so it
    // never persists across reloads. Routing number and bank name are not
    // account-identifying by themselves (a routing number identifies the bank,
    // not the consumer) and may persist in localStorage so returning users
    // don't have to retype them.
    if (sanitized.bank) {
        delete sanitized.bank.account_number;
        delete sanitized.bank.account_number_confirmation;
    }

    // Transient, request-scoped UI state must never survive a reload. In
    // particular stepConnectionError is the "please try again" banner set on a
    // failed CSRF recovery (#5875); persisting it would re-show a scary error on
    // a fresh, healthy page load until the next Continue click cleared it.
    delete sanitized.stepConnectionError;

    return sanitized;
}

/**
 * Build a VuexPersistence instance with expiry metadata + per-profile storage
 * isolation. Each flow (v2, v4) passes its own base `key` so localStorage is
 * isolated per version.
 *
 * @param {Object} options
 * @param {string} options.key  Base localStorage key (e.g. 'leadgen_v2_form').
 * @param {(state: object) => object|null} [options.sanitizeState]  Override the
 *        default leadgen sanitizer if a flow needs a different state shape.
 */
export function createLeadgenVuexPersistence({ key, sanitizeState = sanitizeLeadgenState }) {
    return new VuexPersistence({
        // safeLocalStorage instead of `window.localStorage`: in a storage-denied
        // browser (FB/IG in-app webviews, strict privacy) the `window.localStorage`
        // GETTER ITSELF throws SecurityError. This factory runs at v4 store
        // module-evaluation time, so that throw crashed the bundle before Vue
        // mounted — the blank page (#5873). The custom restoreState/saveState
        // below own the actual reads/writes and already swallow storage errors,
        // so persistence just degrades to Vuex's in-memory state for the session.
        storage: safeLocalStorage,
        key,
        // vuex-persist's save subscriber fires on EVERY commit by default,
        // including clearPersistedFormData itself. That mutation calls
        // localStorage.removeItem() directly (see leadgen-v4/store.js /
        // shared/store.js); without this filter, the subscriber would
        // immediately re-save right after — resurrecting a fresh-but-present
        // entry that makes hasPersistedFormData() report "prior progress" for
        // a lead that was just submitted, re-triggering the resume guard's
        // skip-ahead bug (#5752 follow-up) the next time they re-enter.
        filter: (mutation) => mutation.type !== 'clearPersistedFormData',
        restoreState: (storeKey) => {
            try {
                const storageKey = getStorageKey(storeKey);
                const stored = localStorage.getItem(storageKey);
                if (!stored) {
                    return null;
                }

                const parsed = JSON.parse(stored);

                if (isExpired(parsed)) {
                    localStorage.removeItem(storageKey);
                    return null;
                }

                const { _expiresAt, _timestamp, ...state } = parsed;
                return state;
            } catch (error) {
                console.warn('[Vuex] Error restoring state from localStorage:', error);
                return null;
            }
        },
        saveState: (storeKey, state) => {
            const sanitizedState = sanitizeState(state);

            try {
                const storageKey = getStorageKey(storeKey);

                const payload = {
                    ...sanitizedState,
                    _timestamp: Date.now(),
                    _expiresAt: Date.now() + (STORAGE_EXPIRATION_HOURS * 60 * 60 * 1000),
                };

                localStorage.setItem(storageKey, JSON.stringify(payload));
            } catch (error) {
                console.warn('[Vuex] Error saving state to localStorage:', error);
            }
        },
    });
}

/**
 * Tab-scoped SSN persistence plugin. SSN is stripped from the localStorage
 * snapshot for PCI; this keeps it alive across reloads within the same tab only
 * (sessionStorage dies on tab close). Implemented as a plain Vuex plugin that
 * subscribes to mutations and rehydrates on startup.
 *
 * @param {Object} options
 * @param {string} options.keyBase  Base sessionStorage key (e.g. 'leadgen_v2_ssn').
 */
export function createSsnSessionPlugin({ keyBase }) {
    return (store) => {
        try {
            const k = getStorageKey(keyBase);
            const stored = sessionStorage.getItem(k);
            if (stored) {
                const { ssn } = JSON.parse(stored);
                if (ssn && !store.state.contact.ssn) {
                    store.commit('updateContact', { key: 'ssn', value: ssn });
                }
            }
        } catch { /* ignore */ }

        store.subscribe((mutation, state) => {
            if (mutation.type !== 'updateContact' && mutation.type !== 'updateContactAtOnce') return;
            try {
                const k = getStorageKey(keyBase);
                const ssn = state?.contact?.ssn;
                if (ssn) {
                    sessionStorage.setItem(k, JSON.stringify({ ssn }));
                } else {
                    sessionStorage.removeItem(k);
                }
            } catch { /* ignore */ }
        });
    };
}
